import React, { useMemo, useState, memo } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SaveIcon from "@mui/icons-material/Save";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveIcon from "@mui/icons-material/Remove";
import { apiFetch } from "../../api/apiClient";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";

const createGuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.random() * 16;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return Math.floor(value).toString(16);
  });
};

const createBaselineIdentifiers = () => ({
  project_id: createGuid(),
  part_key: `PART_${Date.now()}`,
});

const createEmptyCustomQuestion = () => ({
  question: "",
  answer: "",
});

const sanitizeCustomQuestions = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => ({
      question: String(item?.question || "").trim(),
      answer: String(item?.answer || "").trim(),
    }))
    .filter((item) => item.question || item.answer);
};

const createBaselineFormState = () => ({
  tower: "",
  commodity: "",
  ...createBaselineIdentifiers(),
  total_cost: "",
  material_cost: "",
  process_cost: "",
  packaging_cost: "",
  ohp_cost: "",
  customQuestions: [createEmptyCustomQuestion()],
});

const isBaselineValue = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "yes";
  }
  return false;
};

const CostTrendsChart = memo(function CostTrendsChart({
  chartData,
  highlightedRunId,
  perlColors,
}) {
  const renderDot = (color) => (props) => {
    if (props.cx == null || props.cy == null) return null;
    const isActive = highlightedRunId === props.payload?.runId;
    const r = isActive ? 6 : 3;
    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={r}
        fill={color}
        stroke={isActive ? "#ffffff" : color}
        strokeWidth={isActive ? 2 : 0}
      />
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#9aa1a6" }}>
        Select runs to view trends.
      </Typography>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chartData} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={perlColors.border} />
        <XAxis dataKey="runId" stroke="#9aa1a6" tick={{ fontSize: 12, fill: "#9aa1a6" }} />
        <YAxis
          stroke="#9aa1a6"
          tick={{ fontSize: 12, fill: "#9aa1a6" }}
          tickFormatter={(v) => Number(v).toFixed(2)}
        />
        <RechartsTooltip />
        <Legend />
        <Line
          type="monotone"
          dataKey="total_cost"
          name="Total"
          stroke="#ff6034"
          strokeWidth={2}
          dot={renderDot("#ff6034")}
        />
        <Line
          type="monotone"
          dataKey="material_cost"
          name="Material"
          stroke="#4aa3ff"
          strokeWidth={2}
          dot={renderDot("#4aa3ff")}
        />
        <Line
          type="monotone"
          dataKey="process_cost"
          name="Process"
          stroke="#7bd67b"
          strokeWidth={2}
          dot={renderDot("#7bd67b")}
        />
        <Line
          type="monotone"
          dataKey="packaging_cost"
          name="Packaging"
          stroke="#f5c542"
          strokeWidth={2}
          dot={renderDot("#f5c542")}
        />
        <Line
          type="monotone"
          dataKey="ohp_cost"
          name="OHP"
          stroke="#ff8f6b"
          strokeWidth={2}
          dot={renderDot("#ff8f6b")}
        />
      </LineChart>
    </ResponsiveContainer>
  );
});

export default function AnalysisPage() {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
    border: "#2D2F31",
  };

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [runOptions, setRunOptions] = useState([]);
  const [runOptionsLoading, setRunOptionsLoading] = useState(false);
  const [runOptionsError, setRunOptionsError] = useState("");
  const [runSearch, setRunSearch] = useState("");
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [highlightedRunId, setHighlightedRunId] = useState(null);
  const [expandAllRuns, setExpandAllRuns] = useState(false);
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedCommodity, setSelectedCommodity] = useState("");
  const [baselineOnlyFilter, setBaselineOnlyFilter] = useState(false);
  const [costTableTab, setCostTableTab] = useState(0);
  const [deltaTolerancePct, setDeltaTolerancePct] = useState(20);
  const [baselineDialogOpen, setBaselineDialogOpen] = useState(false);
  const [baselineSaving, setBaselineSaving] = useState(false);
  const [baselineError, setBaselineError] = useState("");
  const [baselineForm, setBaselineForm] = useState(createBaselineFormState);

  const fetchRunOptions = async () => {
    setRunOptionsLoading(true);
    setRunOptionsError("");
    try {
      const resp = await apiFetch("/api/analysis/run-ids?limit=500");
      const data = await resp.json();
      setRunOptions(Array.isArray(data.runs) ? data.runs : []);
    } catch (err) {
      setRunOptionsError(err?.error || err?.message || "Failed to load run list.");
    } finally {
      setRunOptionsLoading(false);
    }
  };

  const handleOpenAddProjects = () => {
    setAddDialogOpen(true);
    if (runOptions.length === 0 && !runOptionsLoading) {
      fetchRunOptions();
    }
  };

  const handleCloseAddProjects = () => {
    setAddDialogOpen(false);
  };

  const handleOpenAddBaseline = () => {
    setBaselineError("");
    setBaselineForm(createBaselineFormState());
    setBaselineDialogOpen(true);
    if (runOptions.length === 0 && !runOptionsLoading) {
      fetchRunOptions();
    }
  };

  const handleCloseAddBaseline = () => {
    if (baselineSaving) return;
    setBaselineDialogOpen(false);
  };

  const toNullableNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
  };

  const calculatedBaselineTotal = useMemo(() => {
    const components = [
      toNullableNumber(baselineForm.material_cost),
      toNullableNumber(baselineForm.process_cost),
      toNullableNumber(baselineForm.packaging_cost),
      toNullableNumber(baselineForm.ohp_cost),
    ];
    if (components.every((value) => value === null)) return null;
    if (components.some((value) => Number.isNaN(value))) return NaN;
    return components.reduce((sum, value) => sum + (value || 0), 0);
  }, [
    baselineForm.material_cost,
    baselineForm.process_cost,
    baselineForm.packaging_cost,
    baselineForm.ohp_cost,
  ]);

  const customQuestionsForSave = useMemo(
    () => sanitizeCustomQuestions(baselineForm.customQuestions),
    [baselineForm.customQuestions],
  );

  const handleAddCustomQuestionRow = () => {
    setBaselineForm((prev) => ({
      ...prev,
      customQuestions: [...(Array.isArray(prev.customQuestions) ? prev.customQuestions : []), createEmptyCustomQuestion()],
    }));
  };

  const handleRemoveCustomQuestionRow = (indexToRemove) => {
    setBaselineForm((prev) => {
      const rows = Array.isArray(prev.customQuestions) ? prev.customQuestions : [];
      const nextRows = rows.filter((_, idx) => idx !== indexToRemove);
      return {
        ...prev,
        customQuestions: nextRows.length ? nextRows : [createEmptyCustomQuestion()],
      };
    });
  };

  const handleCustomQuestionChange = (index, field, value) => {
    setBaselineForm((prev) => {
      const rows = Array.isArray(prev.customQuestions) ? [...prev.customQuestions] : [createEmptyCustomQuestion()];
      if (!rows[index]) rows[index] = createEmptyCustomQuestion();
      rows[index] = {
        ...rows[index],
        [field]: value,
      };
      return {
        ...prev,
        customQuestions: rows,
      };
    });
  };

  const handleSaveBaseline = async () => {
    setBaselineError("");
    const generatedProjectId = baselineForm.project_id.trim() || createGuid();
    const generatedPartKey = baselineForm.part_key.trim() || `PART_${Date.now()}`;
    const payload = {
      tower: baselineForm.tower.trim(),
      commodity: baselineForm.commodity.trim(),
      project_id: generatedProjectId,
      part_key: generatedPartKey,
      total_cost: Number.isFinite(calculatedBaselineTotal) ? calculatedBaselineTotal : null,
      material_cost: toNullableNumber(baselineForm.material_cost),
      process_cost: toNullableNumber(baselineForm.process_cost),
      packaging_cost: toNullableNumber(baselineForm.packaging_cost),
      ohp_cost: toNullableNumber(baselineForm.ohp_cost),
      customQuestions: customQuestionsForSave,
    };

    if (!payload.tower) {
      setBaselineError("Tower is required.");
      return;
    }
    if (!payload.commodity) {
      setBaselineError("Commodity is required.");
      return;
    }

    const numericFields = [
      "material_cost",
      "process_cost",
      "packaging_cost",
      "ohp_cost",
    ];
    for (const field of numericFields) {
      if (Number.isNaN(payload[field])) {
        setBaselineError(`Invalid numeric value for ${field.replace(/_/g, " ")}.`);
        return;
      }
    }

    setBaselineSaving(true);
    try {
      const resp = await apiFetch("/api/analysis/run-log/baseline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      const newRunId = data?.run_id ?? null;
      await fetchRunOptions();
      if (newRunId !== null && newRunId !== undefined) {
        setSelectedRunIds((prev) => (prev.includes(newRunId) ? prev : [newRunId, ...prev]));
      }
      setBaselineDialogOpen(false);
      setAddDialogOpen(true);
    } catch (err) {
      setBaselineError(err?.error || err?.message || "Failed to save baseline entry.");
    } finally {
      setBaselineSaving(false);
    }
  };

  const toggleRunId = (runId) => {
    setSelectedRunIds((prev) =>
      prev.includes(runId) ? prev.filter((id) => id !== runId) : [...prev, runId],
    );
  };

  const filteredRuns = useMemo(() => {
    const q = runSearch.trim().toLowerCase();
    let rows = runOptions;
    if (baselineOnlyFilter) {
      rows = rows.filter((row) => isBaselineValue(row.baseline ?? row.Baseline));
    }
    if (selectedTower) {
      rows = rows.filter((row) => String(row.tower || "").toLowerCase() === selectedTower);
    }
    if (selectedCommodity) {
      rows = rows.filter(
        (row) => String(row.commodity || "").toLowerCase() === selectedCommodity,
      );
    }
    if (!q) return rows;
    return rows.filter((row) => {
      const blob = [
        row.run_id,
        row.tower,
        row.commodity,
        row.response_status,
        row.productName,
        row.country,
        row.skuWeight,
        row.marketPositioning,
        row.annualVolume,
        isBaselineValue(row.baseline ?? row.Baseline) ? "baseline" : "",
      ]
        .filter((v) => v !== null && v !== undefined)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [runOptions, runSearch, selectedTower, selectedCommodity, baselineOnlyFilter]);

  const towerOptions = useMemo(() => {
    const values = new Set();
    runOptions.forEach((row) => {
      if (row.tower) values.add(String(row.tower));
    });
    return Array.from(values).sort();
  }, [runOptions]);

  const commodityOptions = useMemo(() => {
    const values = new Set();
    runOptions.forEach((row) => {
      if (selectedTower) {
        if (String(row.tower || "").toLowerCase() === selectedTower && row.commodity) {
          values.add(String(row.commodity));
        }
      } else if (row.commodity) {
        values.add(String(row.commodity));
      }
    });
    return Array.from(values).sort();
  }, [runOptions, selectedTower]);

  const baselineCommodityOptions = useMemo(() => {
    const values = new Set();
    runOptions.forEach((row) => {
      if (!row.commodity) return;
      if (baselineForm.tower) {
        if (String(row.tower || "").toLowerCase() === baselineForm.tower.toLowerCase()) {
          values.add(String(row.commodity));
        }
      } else {
        values.add(String(row.commodity));
      }
    });
    return Array.from(values).sort();
  }, [runOptions, baselineForm.tower]);

  const selectedRuns = useMemo(
    () =>
      runOptions.filter((row) => {
        const runId = row.run_id ?? row.runId;
        return selectedRunIds.includes(runId);
      }),
    [runOptions, selectedRunIds],
  );

  const selectedRunsSorted = useMemo(() => {
    return [...selectedRuns].sort((a, b) => {
      const aId = a.run_id ?? a.runId ?? 0;
      const bId = b.run_id ?? b.runId ?? 0;
      return aId - bId;
    });
  }, [selectedRuns]);

  const toggleRunHighlight = (runId) => {
    setHighlightedRunId((prev) => (prev === runId ? null : runId));
  };

  const removeSelectedRun = (runId) => {
    setSelectedRunIds((prev) => prev.filter((id) => id !== runId));
    setHighlightedRunId((prev) => (prev === runId ? null : prev));
  };

  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const chartData = useMemo(() => {
    const rows = selectedRuns.map((row) => ({
      runId: row.run_id ?? row.runId,
      total_cost: toNumber(row.total_cost),
      material_cost: toNumber(row.material_cost),
      process_cost: toNumber(row.process_cost),
      packaging_cost: toNumber(row.packaging_cost),
      ohp_cost: toNumber(row.ohp_cost),
    }));

    return rows.sort((a, b) => (a.runId ?? 0) - (b.runId ?? 0));
  }, [selectedRuns]);

  const chartDataByRunId = useMemo(() => {
    const map = new Map();
    chartData.forEach((row) => {
      map.set(row.runId, row);
    });
    return map;
  }, [chartData]);

  const variationStats = useMemo(() => {
    const series = [
      { key: "total_cost", label: "Total" },
      { key: "material_cost", label: "Material" },
      { key: "process_cost", label: "Process" },
      { key: "packaging_cost", label: "Packaging" },
      { key: "ohp_cost", label: "OHP" },
    ];

    return series.map((s) => {
      const values = chartData.map((d) => d[s.key]).filter((v) => Number.isFinite(v));
      if (values.length < 2) {
        return { ...s, variationPct: null };
      }
      const min = Math.min(...values);
      const max = Math.max(...values);
      const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
      const variationPct = avg ? ((max - min) / avg) * 100 : null;
      return { ...s, variationPct };
    });
  }, [chartData]);

  const firstColWidth = 140;
  const runColWidth = 120;
  const effectiveDeltaTolerancePct = useMemo(() => {
    const n = Number(deltaTolerancePct);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, n);
  }, [deltaTolerancePct]);

  const renderValueWithDelta = (value, baseline, isBaseline) => {
    const hasValue = Number.isFinite(value);
    const hasBaseline = Number.isFinite(baseline);

    if (!hasValue) {
      return <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>-</Typography>;
    }

    if (isBaseline || !hasBaseline) {
      return (
        <Typography sx={{ fontSize: "0.7rem", color: perlColors.textPrimary }}>
          {Number(value).toFixed(2)}
        </Typography>
      );
    }

    const delta = Number(value) - Number(baseline);
    const isUp = delta > 0;
    const isDown = delta < 0;
    const Icon = isUp ? ArrowDropUpIcon : isDown ? ArrowDropDownIcon : RemoveIcon;
    const baselineNumber = Number(baseline);
    const pct = baselineNumber === 0 ? (delta === 0 ? 0 : null) : (delta / baselineNumber) * 100;
    const absPct = Number.isFinite(pct) ? Math.abs(pct) : null;
    const isWithinTolerance = absPct !== null ? absPct <= effectiveDeltaTolerancePct : false;
    const color = absPct === null ? "#9aa1a6" : isWithinTolerance ? "#7bd67b" : "#ff6b6b";

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: "0.7rem", color: perlColors.textPrimary }}>
          {Number(value).toFixed(2)}
        </Typography>
        <Icon fontSize="small" sx={{ color }} />
        <Typography sx={{ fontSize: "0.65rem", color }}>
          {pct === null || !Number.isFinite(pct) ? "-" : `${pct.toFixed(2)}%`}
        </Typography>
      </Box>
    );
  };

  return (
    <SidebarLayout>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "1.05rem", mb: 1 }}>
          Analysis
        </Typography>

        <Box
          sx={{
            border: `1px solid ${perlColors.border}`,
            borderRadius: "10px",
            p: 1,
            display: "flex",
            gap: 1,
            alignItems: "center",
            bgcolor: "rgba(255, 255, 255, 0.02)",
          }}
        >
          <Tooltip title="Add Projects">
            <IconButton
              size="small"
              onClick={handleOpenAddProjects}
              sx={{
                color: perlColors.textPrimary,
                "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
              }}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Baseline Project">
            <IconButton
              size="small"
              onClick={handleOpenAddBaseline}
              sx={{
                color: perlColors.textPrimary,
                "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
              }}
            >
              <PostAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={fetchRunOptions}
              sx={{
                color: perlColors.textPrimary,
                "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear Selected Run IDs">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedRunIds([]);
                setHighlightedRunId(null);
              }}
              sx={{
                color: perlColors.textPrimary,
                "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
              }}
            >
              <ClearAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filter">
            <IconButton
              size="small"
              sx={{
                color: perlColors.textPrimary,
                "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
              }}
            >
              <FilterAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export">
            <IconButton
              size="small"
              sx={{
                color: perlColors.textPrimary,
                "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" sx={{ ml: 1, color: "#9aa1a6" }}>
            Selected: {selectedRunIds.length}
          </Typography>
        </Box>

        <Divider sx={{ my: 2, borderColor: perlColors.border }} />

        <Box sx={{ flex: 1, display: "flex", minHeight: 0, gap: 2 }}>
          <Box
            sx={{
              flex: "0 0 28%",
              maxWidth: "32%",
              minWidth: 220,
              border: `1px solid ${perlColors.border}`,
              borderRadius: "10px",
              p: 2,
              bgcolor: "rgba(255, 255, 255, 0.02)",
              minHeight: 0,
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: perlColors.textPrimary }}>
                Run IDs
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Expand All">
                  <IconButton
                    size="small"
                    onClick={() => setExpandAllRuns(true)}
                    sx={{
                      color: perlColors.textPrimary,
                      border: `1px solid ${perlColors.border}`,
                      "&:hover": { borderColor: "#ff6034", color: "#ff6034" },
                    }}
                  >
                    <UnfoldMoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Collapse All">
                  <IconButton
                    size="small"
                    onClick={() => setExpandAllRuns(false)}
                    sx={{
                      color: perlColors.textPrimary,
                      border: `1px solid ${perlColors.border}`,
                      "&:hover": { borderColor: "#ff6034", color: "#ff6034" },
                    }}
                  >
                    <UnfoldLessIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {selectedRuns.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#9aa1a6" }}>
                No runs selected.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {selectedRuns.map((row, idx) => {
                  const runId = row.run_id ?? row.runId ?? idx;
                  const isExpanded = expandAllRuns || highlightedRunId === runId;
                  return (
                    <Box
                      key={runId}
                      onClick={() => toggleRunHighlight(runId)}
                      sx={{
                        position: "relative",
                        border: `1px solid ${perlColors.border}`,
                        borderRadius: "8px",
                        p: 1,
                        bgcolor: isExpanded
                          ? "rgba(255, 96, 52, 0.12)"
                          : "rgba(255, 255, 255, 0.02)",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: "#ff6034",
                          bgcolor: "rgba(255, 96, 52, 0.08)",
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                        Run {runId}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSelectedRun(runId);
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          color: "#9aa1a6",
                          "&:hover": { color: "#ff6034" },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>

                      <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                        {row.tower || "-"} • {row.commodity || "-"} • {row.response_status || "-"}
                        {isBaselineValue(row.baseline ?? row.Baseline) ? " • Baseline" : ""}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                        User: {row.User || row.user || row.user_email || row.userEmail || "-"}
                      </Typography>

                      {isExpanded && (
                        <Box sx={{ mt: 0.75 }}>
                          <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                            Questions:
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem" }}>
                            {row.productName || "-"} | {row.country || "-"} | {row.skuWeight || "-"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem" }}>
                            {row.marketPositioning || "-"} | {row.annualVolume || "-"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              border: `1px solid ${perlColors.border}`,
              borderRadius: "10px",
              p: 2,
              bgcolor: "rgba(255, 255, 255, 0.02)",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle2" sx={{ color: perlColors.textPrimary, mb: 1 }}>
              Cost Trends
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CostTrendsChart
                  chartData={chartData}
                  highlightedRunId={highlightedRunId}
                  perlColors={perlColors}
                />
              </Box>
              <Box
                sx={{
                  width: 220,
                  borderLeft: `1px solid ${perlColors.border}`,
                  pl: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: "#9aa1a6" }}>
                  Variation % (range vs average)
                </Typography>
                {variationStats.map((s) => (
                  <Box
                    key={s.key}
                    sx={{
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: "8px",
                      px: 1,
                      py: 0.5,
                      fontSize: "0.72rem",
                      color: perlColors.textPrimary,
                      bgcolor: "rgba(255, 255, 255, 0.02)",
                    }}
                  >
                    {s.label}: {s.variationPct === null ? "-" : `${s.variationPct.toFixed(1)}%`}
                  </Box>
                ))}
                <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${perlColors.border}` }}>
                  <Typography variant="caption" sx={{ color: "#9aa1a6" }}>
                    Tolerance % (Ref vs others): {effectiveDeltaTolerancePct.toFixed(1)}%
                  </Typography>
                  <Slider
                    size="small"
                    value={effectiveDeltaTolerancePct}
                    min={0}
                    max={100}
                    step={0.5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${Number(value).toFixed(1)}%`}
                    onChange={(_, value) => {
                      const nextValue = Array.isArray(value) ? value[0] : value;
                      setDeltaTolerancePct(Number(nextValue) || 0);
                    }}
                    sx={{
                      mt: 0.7,
                      color: "#ff6034",
                      "& .MuiSlider-thumb": {
                        width: 12,
                        height: 12,
                      },
                      "& .MuiSlider-valueLabel": {
                        bgcolor: "#ff6034",
                        fontSize: "0.65rem",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 1.5,
                borderTop: `1px solid ${perlColors.border}`,
                pt: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ color: "#9aa1a6" }}>
                  Tables
                </Typography>
                <ToggleButtonGroup
                  value={costTableTab}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) setCostTableTab(newValue);
                  }}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      color: perlColors.textPrimary,
                      borderColor: perlColors.border,
                      fontSize: "0.7rem",
                      textTransform: "none",
                      px: 1.5,
                      py: 0.25,
                      "&.Mui-selected": {
                        bgcolor: "rgba(255, 96, 52, 0.2)",
                        color: "#ff6034",
                        "&:hover": {
                          bgcolor: "rgba(255, 96, 52, 0.3)",
                        },
                      },
                      "&:hover": {
                        bgcolor: "rgba(255, 96, 52, 0.1)",
                      },
                    },
                  }}
                >
                  <ToggleButton value={0}>Cost</ToggleButton>
                  <ToggleButton value={1}>Questions</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ mt: 0.75, height: 240 }}>
                {costTableTab === 0 ? (
                  <TableContainer
                    component={Paper}
                    sx={{
                      height: "100%",
                      bgcolor: "transparent",
                      boxShadow: "none",
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: "8px",
                      overflowX: "auto",
                      overflowY: "scroll",
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        tableLayout: "fixed",
                        minWidth: firstColWidth + Math.max(selectedRunsSorted.length, 1) * runColWidth,
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              bgcolor: "#2D2F31",
                              color: perlColors.textPrimary,
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              py: 0.5,
                              height: 32,
                              width: firstColWidth,
                              minWidth: firstColWidth,
                              maxWidth: firstColWidth,
                            }}
                          >
                            Cost
                          </TableCell>
                          {selectedRunsSorted.map((row, idx) => {
                            const runId = row.run_id ?? row.runId ?? idx;
                            return (
                              <TableCell
                                key={`run-col-${runId}`}
                                sx={{
                                  bgcolor: "#2D2F31",
                                  color: perlColors.textPrimary,
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  whiteSpace: "nowrap",
                                  py: 0.5,
                                  height: 32,
                                  width: runColWidth,
                                  minWidth: runColWidth,
                                  maxWidth: runColWidth,
                                }}
                              >
                                Run {runId}
                                {idx === 0 ? " (Ref)" : ""}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          { key: "total_cost", label: "Total" },
                          { key: "material_cost", label: "Material" },
                          { key: "process_cost", label: "Process" },
                          { key: "packaging_cost", label: "Packaging" },
                          { key: "ohp_cost", label: "OHP" },
                        ].map((rowDef) => {
                          const baselineRunId =
                            selectedRunsSorted[0]?.run_id ?? selectedRunsSorted[0]?.runId;
                          const baselineRow = chartDataByRunId.get(baselineRunId);
                          const baselineValue = baselineRow?.[rowDef.key];
                          return (
                            <TableRow key={rowDef.key} sx={{ height: 32 }}>
                              <TableCell
                                sx={{
                                  color: perlColors.textPrimary,
                                  fontSize: "0.7rem",
                                  width: firstColWidth,
                                  minWidth: firstColWidth,
                                  maxWidth: firstColWidth,
                                  py: 0.5,
                                  height: 32,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {rowDef.label}
                              </TableCell>
                              {selectedRunsSorted.map((run, runIdx) => {
                                const runId = run.run_id ?? run.runId ?? runIdx;
                                const rowData = chartDataByRunId.get(runId);
                                const val = rowData?.[rowDef.key];
                                return (
                                  <TableCell
                                    key={`${rowDef.key}-${runId}`}
                                    sx={{
                                      color: perlColors.textPrimary,
                                      fontSize: "0.7rem",
                                      py: 0.5,
                                      height: 32,
                                      width: runColWidth,
                                      minWidth: runColWidth,
                                      maxWidth: runColWidth,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {renderValueWithDelta(val, baselineValue, runIdx === 0)}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      height: "100%",
                      bgcolor: "transparent",
                      boxShadow: "none",
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: "8px",
                      overflowX: "auto",
                      overflowY: "scroll",
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        tableLayout: "fixed",
                        minWidth: firstColWidth + Math.max(selectedRunsSorted.length, 1) * runColWidth,
                      }}
                    >
                      <TableHead>
                        <TableRow>
                        <TableCell
                          sx={{
                            bgcolor: "#2D2F31",
                            color: perlColors.textPrimary,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            whiteSpace: "nowrap",
                            py: 0.5,
                            height: 32,
                            width: firstColWidth,
                            minWidth: firstColWidth,
                            maxWidth: firstColWidth,
                          }}
                        >
                          Question
                        </TableCell>
                        {selectedRunsSorted.map((row, idx) => {
                          const runId = row.run_id ?? row.runId ?? idx;
                          return (
                            <TableCell
                              key={runId}
                              sx={{
                                bgcolor: "#2D2F31",
                                color: perlColors.textPrimary,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                whiteSpace: "nowrap",
                                py: 0.5,
                                height: 32,
                                width: runColWidth,
                                minWidth: runColWidth,
                                maxWidth: runColWidth,
                              }}
                            >
                              Run {runId}
                              {idx === 0 ? " (Ref)" : ""}
                            </TableCell>
                          );
                        })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          { key: "productName", label: "Product Name" },
                          { key: "country", label: "Country" },
                          { key: "skuWeight", label: "SKU Weight" },
                          { key: "marketPositioning", label: "Market Positioning" },
                          { key: "annualVolume", label: "Annual Volume" },
                        ].map((q) => {
                          const baselineValue = selectedRunsSorted[0]?.[q.key];
                          const normalize = (v) =>
                            v === null || v === undefined ? "" : String(v).trim().toLowerCase();
                          const baselineNorm = normalize(baselineValue);

                          return (
                          <TableRow key={q.key} sx={{ height: 32 }}>
                            <TableCell
                              sx={{
                                color: perlColors.textPrimary,
                                fontSize: "0.7rem",
                                whiteSpace: "nowrap",
                                py: 0.5,
                                height: 32,
                                width: firstColWidth,
                                minWidth: firstColWidth,
                                maxWidth: firstColWidth,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {q.label}
                            </TableCell>
                            {selectedRunsSorted.map((row, idx) => {
                              const runId = row.run_id ?? row.runId ?? idx;
                              const value = row[q.key] ?? "-";
                              const isRef = idx === 0;
                              const isDiff =
                                !isRef && normalize(value) !== baselineNorm && baselineNorm !== "";
                              return (
                                <TableCell
                                  key={`${q.key}-${runId}`}
                                  sx={{
                                    color: perlColors.textPrimary,
                                    fontSize: "0.7rem",
                                    bgcolor: isDiff ? "rgba(255, 96, 52, 0.12)" : "transparent",
                                    py: 0.5,
                                    height: 32,
                                    width: runColWidth,
                                    minWidth: runColWidth,
                                    maxWidth: runColWidth,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {value || "-"}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Dialog
          open={baselineDialogOpen}
          onClose={handleCloseAddBaseline}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: perlColors.background,
              color: perlColors.textPrimary,
              border: `1px solid ${perlColors.border}`,
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Add Baseline Project</DialogTitle>
          <DialogContent sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: perlColors.textPrimary }}>
              Run Metadata
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel
                  sx={{
                    color: perlColors.textPrimary,
                    "&.Mui-focused": { color: "#ff6034" },
                  }}
                >
                  Tower
                </InputLabel>
                <Select
                  value={baselineForm.tower}
                  onChange={(e) =>
                    setBaselineForm((prev) => ({
                      ...prev,
                      tower: e.target.value,
                      commodity: "",
                    }))
                  }
                  label="Tower"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: perlColors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    ".MuiSvgIcon-root": { color: perlColors.textPrimary },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#2D2F31",
                        color: perlColors.textPrimary,
                      },
                    },
                  }}
                >
                  {towerOptions.map((tower) => (
                    <MenuItem key={tower} value={tower}>
                      {tower}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel
                  sx={{
                    color: perlColors.textPrimary,
                    "&.Mui-focused": { color: "#ff6034" },
                  }}
                >
                  Commodity
                </InputLabel>
                <Select
                  value={baselineForm.commodity}
                  onChange={(e) => {
                    const nextCommodity = e.target.value;
                    setBaselineForm((prev) => ({
                      ...prev,
                      commodity: nextCommodity,
                    }));
                  }}
                  label="Commodity"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: perlColors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    ".MuiSvgIcon-root": { color: perlColors.textPrimary },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#2D2F31",
                        color: perlColors.textPrimary,
                      },
                    },
                  }}
                >
                  {baselineCommodityOptions.map((commodity) => (
                    <MenuItem key={commodity} value={commodity}>
                      {commodity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <TextField
                size="small"
                label="Project ID (Auto)"
                value={baselineForm.project_id}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                  "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                }}
              />
              <TextField
                size="small"
                label="Part Key (Auto)"
                value={baselineForm.part_key}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                  "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                }}
              />
            </Box>

            <Divider sx={{ borderColor: perlColors.border, opacity: 0.8 }} />
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: perlColors.textPrimary }}>
              Cost Breakdown
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              <TextField
                size="small"
                type="number"
                label="Total Cost (Auto)"
                value={Number.isFinite(calculatedBaselineTotal) ? calculatedBaselineTotal.toFixed(6) : ""}
                InputProps={{ readOnly: true }}
                inputProps={{ step: "0.000001" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                  "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                }}
              />
              <TextField
                size="small"
                type="number"
                label="Material Cost"
                value={baselineForm.material_cost}
                onChange={(e) =>
                  setBaselineForm((prev) => ({
                    ...prev,
                    material_cost: e.target.value,
                  }))
                }
                inputProps={{ step: "0.000001" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                  "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                }}
              />
              <TextField
                size="small"
                type="number"
                label="Process Cost"
                value={baselineForm.process_cost}
                onChange={(e) =>
                  setBaselineForm((prev) => ({
                    ...prev,
                    process_cost: e.target.value,
                  }))
                }
                inputProps={{ step: "0.000001" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                  "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                }}
              />
              <TextField
                size="small"
                type="number"
                label="Packaging Cost"
                value={baselineForm.packaging_cost}
                onChange={(e) =>
                  setBaselineForm((prev) => ({
                    ...prev,
                    packaging_cost: e.target.value,
                  }))
                }
                inputProps={{ step: "0.000001" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                  "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                }}
              />
              <TextField
                size="small"
                type="number"
                label="OHP Cost"
                value={baselineForm.ohp_cost}
                onChange={(e) =>
                  setBaselineForm((prev) => ({
                    ...prev,
                    ohp_cost: e.target.value,
                  }))
                }
                inputProps={{ step: "0.000001" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                  "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                }}
              />
            </Box>

            <Divider sx={{ borderColor: perlColors.border, opacity: 0.8 }} />
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: perlColors.textPrimary }}>
              Questions
            </Typography>

            <Box
              sx={{
                border: `1px solid ${perlColors.border}`,
                borderRadius: "8px",
                p: 1.2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ fontSize: "0.75rem", color: "#9aa1a6", fontWeight: 600 }}>
                  Dynamic Questions & Answers
                </Typography>
                <Tooltip title="Add Question">
                  <IconButton
                    size="small"
                    onClick={handleAddCustomQuestionRow}
                    sx={{
                      color: "#ff6034",
                      border: `1px solid ${perlColors.border}`,
                      borderRadius: "6px",
                      width: 30,
                      height: 30,
                      "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", borderColor: "#ff6034" },
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {(Array.isArray(baselineForm.customQuestions) ? baselineForm.customQuestions : []).map(
                (qa, idx) => (
                  <Box
                    key={`qa-row-${idx}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" },
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      label={`Question ${idx + 1}`}
                      value={qa?.question || ""}
                      onChange={(e) => handleCustomQuestionChange(idx, "question", e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: perlColors.textPrimary,
                          "& fieldset": { borderColor: perlColors.border },
                          "&:hover fieldset": { borderColor: "#ff6034" },
                          "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                        },
                        "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                      }}
                    />
                    <TextField
                      size="small"
                      label={`Answer ${idx + 1}`}
                      value={qa?.answer || ""}
                      onChange={(e) => handleCustomQuestionChange(idx, "answer", e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: perlColors.textPrimary,
                          "& fieldset": { borderColor: perlColors.border },
                          "&:hover fieldset": { borderColor: "#ff6034" },
                          "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                        },
                        "& .MuiInputLabel-root": { color: perlColors.textPrimary },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveCustomQuestionRow(idx)}
                      sx={{
                        color: perlColors.textSecondary || "#9aa1a6",
                        border: `1px solid ${perlColors.border}`,
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": { color: "#ff6034", borderColor: "#ff6034" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ),
              )}
            </Box>

            {baselineError && <Alert severity="error">{baselineError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Tooltip title="Cancel">
              <span>
                <IconButton
                  onClick={handleCloseAddBaseline}
                  disabled={baselineSaving}
                  sx={{
                    color: perlColors.textPrimary,
                    border: `1px solid ${perlColors.border}`,
                    borderRadius: "8px",
                    width: 34,
                    height: 34,
                    "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={baselineSaving ? "Saving..." : "Save Baseline"}>
              <span>
                <IconButton
                  onClick={handleSaveBaseline}
                  disabled={baselineSaving}
                  sx={{
                    color: "#fff",
                    bgcolor: "#ff6034",
                    borderRadius: "8px",
                    width: 34,
                    height: 34,
                    "&:hover": { bgcolor: "#e55530" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,96,52,0.45)",
                      color: "rgba(255,255,255,0.75)",
                    },
                  }}
                >
                  {baselineSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </DialogActions>
        </Dialog>

        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddProjects}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: perlColors.background,
              color: perlColors.textPrimary,
              border: `1px solid ${perlColors.border}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Add Projects</Typography>
            <Tooltip title="Refresh List">
              <IconButton
                size="small"
                onClick={fetchRunOptions}
                sx={{
                  color: perlColors.textPrimary,
                  "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by run id, tower, commodity, status, or question fields..."
              value={runSearch}
              onChange={(e) => setRunSearch(e.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  color: perlColors.textPrimary,
                  "& fieldset": { borderColor: perlColors.border },
                  "&:hover fieldset": { borderColor: "#ff6034" },
                  "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                },
              }}
            />

            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 200, flex: "1 1 200px" }}>
                <InputLabel
                  sx={{
                    color: perlColors.textPrimary,
                    "&.Mui-focused": { color: "#ff6034" },
                  }}
                >
                  Tower
                </InputLabel>
                <Select
                  value={selectedTower}
                  onChange={(e) => {
                    setSelectedTower(e.target.value);
                    setSelectedCommodity("");
                  }}
                  label="Tower"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: perlColors.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff6034",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff6034",
                    },
                    ".MuiSvgIcon-root": {
                      color: perlColors.textPrimary,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#2D2F31",
                        color: perlColors.textPrimary,
                        "& .MuiMenuItem-root": {
                          "&:hover": { bgcolor: "rgba(255, 96, 52, 0.1)" },
                          "&.Mui-selected": {
                            bgcolor: "rgba(255, 96, 52, 0.2)",
                            "&:hover": { bgcolor: "rgba(255, 96, 52, 0.3)" },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Towers</MenuItem>
                  {towerOptions.map((tower) => (
                    <MenuItem key={tower} value={tower.toLowerCase()}>
                      {tower}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 220, flex: "1 1 220px" }}>
                <InputLabel
                  sx={{
                    color: perlColors.textPrimary,
                    "&.Mui-focused": { color: "#ff6034" },
                  }}
                >
                  Commodity
                </InputLabel>
                <Select
                  value={selectedCommodity}
                  onChange={(e) => setSelectedCommodity(e.target.value)}
                  label="Commodity"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: perlColors.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff6034",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff6034",
                    },
                    ".MuiSvgIcon-root": {
                      color: perlColors.textPrimary,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#2D2F31",
                        color: perlColors.textPrimary,
                        "& .MuiMenuItem-root": {
                          "&:hover": { bgcolor: "rgba(255, 96, 52, 0.1)" },
                          "&.Mui-selected": {
                            bgcolor: "rgba(255, 96, 52, 0.2)",
                            "&:hover": { bgcolor: "rgba(255, 96, 52, 0.3)" },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Commodities</MenuItem>
                  {commodityOptions.map((commodity) => (
                    <MenuItem key={commodity} value={commodity.toLowerCase()}>
                      {commodity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                sx={{
                  minWidth: 180,
                  flex: "0 1 180px",
                  display: "flex",
                  alignItems: "center",
                  border: `1px solid ${perlColors.border}`,
                  borderRadius: "6px",
                  px: 0.5,
                  height: 40,
                }}
              >
                <Checkbox
                  size="small"
                  checked={baselineOnlyFilter}
                  onChange={(e) => setBaselineOnlyFilter(e.target.checked)}
                  sx={{
                    color: "#9aa1a6",
                    "&.Mui-checked": { color: "#ff6034" },
                  }}
                />
                <Typography sx={{ fontSize: "0.78rem", color: perlColors.textPrimary }}>
                  Baseline Only
                </Typography>
              </Box>
            </Box>

            {runOptionsLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {runOptionsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {runOptionsError}
              </Alert>
            )}

            {!runOptionsLoading && !runOptionsError && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 1,
                }}
              >
                {filteredRuns.map((row, idx) => {
                  const runId = row.run_id ?? row.runId ?? idx;
                  const isSelected = selectedRunIds.includes(runId);
                  return (
                    <Box
                      key={runId}
                      onClick={() => toggleRunId(runId)}
                      sx={{
                        border: `1px solid ${perlColors.border}`,
                        borderRadius: "8px",
                        p: 1,
                        cursor: "pointer",
                        bgcolor: isSelected ? "rgba(255, 96, 52, 0.12)" : "transparent",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#ff6034" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                          Run {runId}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleRunId(runId)}
                          sx={{
                            color: "#9aa1a6",
                            "&.Mui-checked": { color: "#ff6034" },
                          }}
                        />
                      </Box>

                      <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                        {row.tower || "-"} • {row.commodity || "-"} • {row.response_status || "-"}
                        {isBaselineValue(row.baseline ?? row.Baseline) ? " • Baseline" : ""}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                        User: {row.User || row.user || row.user_email || row.userEmail || "-"}
                      </Typography>

                      <Box sx={{ mt: 0.5 }}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                          Questions:
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem" }}>
                          {row.productName || "-"} | {row.country || "-"} | {row.skuWeight || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem" }}>
                          {row.marketPositioning || "-"} | {row.annualVolume || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseAddProjects}
              sx={{
                color: perlColors.textPrimary,
                "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCloseAddProjects}
              sx={{
                bgcolor: "#ff6034",
                color: "#fff",
                textTransform: "none",
                "&:hover": { bgcolor: "#e55530" },
              }}
            >
              Add Selected
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
}
